import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@this/constants/themes';
import {
  workflowSelectorDropdownItemCss,
  workflowSelectorDropdownListCss,
  workflowSelectorDropdownPanelCss,
  workflowSelectorSearchInputCss
} from '@this/components/shared/workflow_selector_appearance';

import { reportError } from '@this/lib/bugsnag';
import type Department from '@this/domain/department/department2';

type Props = {
  departments: Department[];
  onSelect: (department: Department) => void;
  disabledAddress: boolean;
  workflowFormWidth: string;
};

const DepartmentsList: React.FC<Props> = ({ departments, onSelect, disabledAddress, workflowFormWidth }) => {
  const [filteredDepartments, setFilteredDepartments] = useState<Department[]>(departments);

  useEffect(() => {
    const el = document.getElementById('departments-list-input');
    if (el) el.focus();
  }, []);

  useEffect(() => {
    setFilteredDepartments(departments);
  }, [departments]);

  const handleSelect = useCallback(
    (department: Department) => () => {
      onSelect(department);
    },
    [onSelect]
  );

  const handleSearchDepartments = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const keyword = (e.target as HTMLInputElement).value;
      const filtered = departments.filter(
        department =>
          (department.code ?? '').indexOf(keyword) >= 0 || (department.name ?? '').indexOf(keyword) >= 0
      );
      setFilteredDepartments(filtered);
    },
    [departments]
  );

  try {
    return (
      <DepartmentsListDropdownPanel workflowFormWidth={workflowFormWidth}>
        <DepartmentListsInput
          type="text"
          id="departments-list-input"
          data-testid="reserve-confirm-input-shinkansen-department-search-input"
          defaultValue=""
          placeholder="検索"
          onKeyUp={handleSearchDepartments}
        />
        <DepartmentsListUl>
          {filteredDepartments.map((department, i) =>
            department.noAddress && disabledAddress ? (
              <DepartmentsListItemDisabled
                key={department.id}
                data-testid={`reserve-confirm-input-shinkansen-department-option-disabled-${i}`}
              >
                <DepartmentsListItemInfo>
                  <p>{department.description}</p>
                </DepartmentsListItemInfo>
              </DepartmentsListItemDisabled>
            ) : (
              <DepartmentsListItemEnabled
                key={department.id}
                data-testid={`reserve-confirm-input-shinkansen-department-option-${i}`}
                onClick={handleSelect(department)}
              >
                <DepartmentsListItemInfo>
                  <p>{disabledAddress ? department.description : department.descriptionWithCode}</p>
                </DepartmentsListItemInfo>
              </DepartmentsListItemEnabled>
            )
          )}
        </DepartmentsListUl>
      </DepartmentsListDropdownPanel>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const DepartmentsListDropdownPanel = styled.div<{ workflowFormWidth?: string }>`
  ${workflowSelectorDropdownPanelCss};
  width: ${props => props.workflowFormWidth || '420px'};
`;

const DepartmentListsInput = styled.input`
  ${workflowSelectorSearchInputCss};
`;

const DepartmentsListUl = styled.ul`
  ${workflowSelectorDropdownListCss};
`;

const DepartmentsListItemDisabled = styled.li`
  ${workflowSelectorDropdownItemCss};
  background: #eee;
  cursor: default;
  overflow-x: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DepartmentsListItemEnabled = styled.li`
  ${workflowSelectorDropdownItemCss};
  overflow-x: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DepartmentsListItemInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export default DepartmentsList;
